import { formatPhoneNumberForDisplay } from "../../../util/helpers";

// The chat keeps the chosen sender as one string: the number for SMS, `<number>-whatsapp` for WhatsApp.
export const WA_SUFFIX = "-whatsapp";
export const valueOf = (number, channel) => (channel === "whatsapp" ? `${number}${WA_SUFFIX}` : String(number));
export const parseValue = value => {
    const text = String(value || "");
    return text.endsWith(WA_SUFFIX)
        ? {number: text.slice(0, -WA_SUFFIX.length), channel: "whatsapp"}
        : {number: text, channel: "sms"};
};

const digitsOf = value => String(value || "").replace(/\D/g, "");

// One entry per account number. A customName is an alphanumeric sender ID (e.g. "2WAY"): SMS only, no replies.
export const buildSenders = (list = []) =>
    (list || [])
        .map(item => {
            const capabilities = item.capabilities || [];
            return {
                number: String(item.number),
                isDefault: Boolean(item.isDefault),
                isSenderId: Boolean(item.customName),
                title: item.customName || formatPhoneNumberForDisplay(String(item.number)),
                label: item.label || "",
                sms: capabilities.includes("sms"),
                whatsapp: capabilities.includes("whatsapp"),
            };
        })
        .filter(sender => sender.sms || sender.whatsapp);

const isWhatsapp = message => message.platform === "whatsapp" || message.channel === "whatsapp";
const isConversationMessage = message => ["sms", "mms", "whatsapp"].includes(message.channel) || message.platform === "whatsapp";

// Which of the account's numbers this customer used: the last number they wrote to (and on which channel), and the
// last one the team replied from. Newest first, without duplicates.
export const customerSenders = (messages = [], senders = []) => {
    const known = new Set(senders.map(sender => sender.number));
    const sorted = [...(messages || [])]
        .filter(message => message && !message.pending && !message.failed && isConversationMessage(message))
        .sort((a, b) => new Date(b.time) - new Date(a.time));
    const result = [];
    const add = (number, channel, reason, time) => {
        const key = String(number || "");
        if (!known.has(key) || result.some(item => item.number === key)) return;
        result.push({number: key, channel, reason, time});
    };
    const lastIn = sorted.find(message => message.direction === "in");
    if (lastIn) add(lastIn.to, isWhatsapp(lastIn) ? "whatsapp" : "sms", "wrote", lastIn.time);
    const lastOut = sorted.find(message => message.direction !== "in");
    if (lastOut) add(lastOut.from, isWhatsapp(lastOut) ? "whatsapp" : "sms", "replied", lastOut.time);
    return result;
};

// The sender a conversation should open with: where the customer last wrote, else where we last replied from,
// else the account's default.
export const initialSenderValue = (messages, list) => {
    const senders = buildSenders(list);
    const [first] = customerSenders(messages, senders);
    if (first) {
        const sender = senders.find(item => item.number === first.number);
        const channel = sender[first.channel] ? first.channel : sender.sms ? "sms" : "whatsapp";
        return valueOf(first.number, channel);
    }
    return "";
};

// Search by digits (any formatting, with or without +/country code) or by name.
export const matches = (sender, query) => {
    const text = String(query || "").trim().toLowerCase();
    if (!text) return true;
    const digits = digitsOf(text);
    if (digits && digits.length === text.replace(/[\s+()-]/g, "").length) {
        return digitsOf(sender.number).includes(digits) || digitsOf(sender.title).includes(digits);
    }
    return [sender.title, sender.label, sender.number].some(value => String(value || "").toLowerCase().includes(text));
};

// Splits the shown text into [plain, marked, plain] parts for highlighting a digit search inside a formatted number.
export const highlightParts = (shown, query) => {
    const text = String(query || "").trim();
    if (!text) return [shown];
    const digits = digitsOf(text);
    const isDigitQuery = digits && digits.length === text.replace(/[\s+()-]/g, "").length;
    if (!isDigitQuery) {
        const at = shown.toLowerCase().indexOf(text.toLowerCase());
        return at < 0 ? [shown] : [shown.slice(0, at), {mark: shown.slice(at, at + text.length)}, shown.slice(at + text.length)];
    }
    const positions = [];
    for (let i = 0; i < shown.length; i++) if (/\d/.test(shown[i])) positions.push(i);
    const at = positions.map(i => shown[i]).join("").indexOf(digits);
    if (at < 0) return [shown];
    const start = positions[at];
    const end = positions[at + digits.length - 1] + 1;
    return [shown.slice(0, start), {mark: shown.slice(start, end)}, shown.slice(end)];
};

const channelFamily = message => (isWhatsapp(message) ? "whatsapp" : "sms");
// Which of the account's numbers a message used: the one the customer wrote to, or the one we sent from.
const ourNumber = message => String((message.direction === "in" ? message.to : message.from) || "");

// Points where the customer changed how they reach us: wrote on another channel (SMS → WhatsApp) or to another of
// the account's numbers than the conversation used just before. Keyed by the customer's message that did it.
// Our own sender changes are the operator's choice and are not marked.
export const conversationSwitches = (messages = []) => {
    const sorted = [...(messages || [])]
        .filter(message => message && !message.pending && !message.failed && isConversationMessage(message))
        .sort((a, b) => new Date(a.time) - new Date(b.time));
    const result = [];
    let previous = null;
    sorted.forEach(message => {
        const current = {number: ourNumber(message), channel: channelFamily(message), customName: message.direction === "in" ? null : message.customName};
        if (previous && message.direction === "in") {
            const base = {messageId: message.messageId || message._id, time: message.time, number: current.number, before: previous};
            const numberChanged = Boolean(current.number && previous.number && current.number !== previous.number);
            // "channel": SMS ⇄ WhatsApp (numberChanged when also to another of our numbers); "number": same channel,
            // another of our numbers.
            if (current.channel !== previous.channel) result.push({...base, type: "channel", channel: current.channel, numberChanged});
            else if (numberChanged) result.push({...base, type: "number", channel: current.channel, numberChanged});
        }
        previous = current;
    });
    return result;
};

// The latest switch we have not replied to yet (no message of ours after it), or null.
export const unansweredSwitch = (messages = []) => {
    const switches = conversationSwitches(messages);
    const last = switches[switches.length - 1];
    if (!last) return null;
    const at = new Date(last.time).getTime();
    const replied = (messages || []).some(message => message && message.direction !== "in" && !message.failed
        && isConversationMessage(message) && new Date(message.time).getTime() > at);
    return replied ? null : last;
};

// Hours left to reply on WhatsApp freely (24 h from the customer's last WhatsApp message), or null when closed.
export const whatsappWindowHours = (messages = [], now = Date.now()) => {
    const lastIn = [...(messages || [])]
        .filter(message => message && message.direction === "in" && isWhatsapp(message))
        .sort((a, b) => new Date(b.time) - new Date(a.time))[0];
    if (!lastIn) return null;
    const left = 24 - (now - new Date(lastIn.time).getTime()) / 36e5;
    return left > 0 ? Math.max(1, Math.floor(left)) : null;
};

// How to show one of the account's numbers: its sender ID / label, else the formatted number.
export const senderTitle = (number, list = [], customName) => {
    const item = (list || []).find(entry => String(entry.number) === String(number));
    return (item && item.customName) || customName || formatPhoneNumberForDisplay(String(number || ""));
};
